// DATA-70 — Realtime freshness cursor hook.
//
// Polls /api/realtime/cursor every 30s. Caller subscribes to a single domain
// (e.g. 'insights', 'bod_snapshots') and receives the latest timestamp +
// a `bumped` boolean that flips true once when the cursor advances since
// the previous poll. Pages use the bumped flag to decide when to refetch
// their heavier domain endpoints.

import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/apiFetch";

export type RealtimeDomain =
  | "aggregates"
  | "insights"
  | "alerts"
  | "bod_snapshots"
  | "cross_org"
  | "rr_digest"
  | "insights_digest"
  | "strategic_review";

export interface RealtimeCursor {
  as_of: string;
  aggregates: string | null;
  insights: string | null;
  alerts: string | null;
  bod_snapshots: string | null;
  cross_org: string | null;
  rr_digest: string | null;
  insights_digest: string | null;
  strategic_review: string | null;
  llm_calls_24h: number;
}

const POLL_INTERVAL_MS = 30_000;

export function useRealtimeCursor(domain: RealtimeDomain): {
  updatedAt: string | null;
  bumped: boolean;
  cursor: RealtimeCursor | null;
} {
  const [cursor, setCursor] = useState<RealtimeCursor | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [bumped, setBumped] = useState(false);
  const prevValueRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function poll() {
      try {
        const r = await apiFetch("/api/realtime/cursor", {
          credentials: "same-origin",
        });
        if (!r.ok) return;
        const next = (await r.json()) as RealtimeCursor;
        if (cancelled) return;
        const nextVal = (next[domain] as string | null) ?? null;
        const prev = prevValueRef.current;
        if (prev !== null && nextVal !== prev) {
          setBumped(true);
        }
        prevValueRef.current = nextVal;
        setUpdatedAt(nextVal);
        setCursor(next);
      } catch {
        // ignore; will retry on the next tick
      }
    }

    poll();
    timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [domain]);

  return { updatedAt, bumped, cursor };
}

/** Reset the bumped flag after the page has refetched in response to it. */
export function useAcknowledgeBump(bumped: boolean, ack: () => void): void {
  useEffect(() => {
    if (bumped) ack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bumped]);
}
